import { http } from './http';
import { ROUTES } from './config';
import type { Blog, BlogInput, BlogPublicPage } from '../types/domain';

/** BlogController client (manager+ authoring; /public and /categories are anonymous). */
export const blogApi = {
  /** Authoring view — includes unpublished drafts, no pagination (server returns everything). */
  list(): Promise<Blog[]> {
    return http.get(ROUTES.blog.base);
  },

  /** Anonymous: published posts, newest first, excerpt only — the storefront blog listing. */
  getPublic(params: { category?: string; page?: number; pageSize?: number } = {}): Promise<BlogPublicPage> {
    const qs = new URLSearchParams();
    if (params.category) qs.set('category', params.category);
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    const query = qs.toString();
    return http.get(`${ROUTES.blog.public}${query ? `?${query}` : ''}`, { anonymous: true });
  },

  /** Anonymous: one published post by slug, with full body — the storefront blog detail page. */
  getPublicBySlug(slug: string): Promise<Blog> {
    return http.get(`${ROUTES.blog.public}/${encodeURIComponent(slug)}`, { anonymous: true });
  },

  get(id: string): Promise<Blog> {
    return http.get(`${ROUTES.blog.base}/${id}`);
  },

  create(body: BlogInput): Promise<{ id: string; slug: string }> {
    return http.post(ROUTES.blog.base, body);
  },

  update(id: string, body: BlogInput): Promise<void> {
    return http.put(`${ROUTES.blog.base}/${id}`, body);
  },

  remove(id: string): Promise<void> {
    return http.delete(`${ROUTES.blog.base}/${id}`);
  },

  /** Distinct categories among published posts — anonymous. */
  categories(): Promise<string[]> {
    return http.get(ROUTES.blog.categories, { anonymous: true });
  },
};
